import { BarList, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title } from '@tremor/react';
import type { ServiceOrder } from '../../api/types';
import { t } from '../../lib/i18n';

export type StatusKey = 'A' | 'S' | 'W' | 'H' | 'C' | 'bko';
export interface ResumenKpis {
  woCount: number; laborMxn: number; partsMxn: number; totalMxn: number;
  aro: number; laborRate: number; actHrs: number; jobs: number; techs: number;
  status: Record<StatusKey, number>;
  category: { name: string; value: number }[];
  topTechs: { tech: string; name: string; revenue: number; est: number; act: number }[];
}

/** Effective status column: backorder wins, else the ss-status char (unknown → 'W'). */
export function statusKey(o: Pick<ServiceOrder, 'wo_status' | 'backorder'>): StatusKey {
  if (o.backorder) return 'bko';
  return (['A', 'S', 'W', 'H', 'C'].includes(o.wo_status) ? o.wo_status : 'W') as StatusKey;
}

export function computeResumen(orders: ServiceOrder[]): ResumenKpis {
  const status: Record<StatusKey, number> = { A: 0, S: 0, W: 0, H: 0, C: 0, bko: 0 };
  const category = new Map<string, number>();
  const techs = new Map<string, { tech: string; name: string; revenue: number; est: number; act: number }>();
  const woParts = new Map<string, number>();   // per-WO parts (echoed value, taken once)
  const woSeen = new Set<string>();
  let laborMxn = 0, actHrs = 0, jobs = 0;
  for (const o of orders) {
    const wo = `${o.sid}-${o.tnum}`;
    laborMxn += o.price_mxn; actHrs += o.act_hrs; jobs += 1;
    woParts.set(wo, o.parts_sell_mxn);          // same value on every line of the WO
    if (!woSeen.has(wo)) { woSeen.add(wo); status[statusKey(o)] += 1; }
    category.set(o.job_category || o.job_desc || o.job_code || '—', (category.get(o.job_category || o.job_desc || o.job_code || '—') ?? 0) + o.price_mxn);
    const key = o.tech || '—';
    const cur = techs.get(key) ?? { tech: key, name: o.tech_name || o.tech || '—', revenue: 0, est: 0, act: 0 };
    cur.revenue += o.price_mxn; cur.est += o.est_hrs; cur.act += o.act_hrs; techs.set(key, cur);
  }
  const partsMxn = [...woParts.values()].reduce((s, v) => s + v, 0);
  const woCount = woSeen.size;
  const totalMxn = laborMxn + partsMxn;
  return {
    woCount, laborMxn, partsMxn, totalMxn,
    aro: woCount > 0 ? totalMxn / woCount : 0,
    laborRate: actHrs > 0 ? laborMxn / actHrs : 0,
    actHrs, jobs, techs: techs.size, status,
    category: [...category.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 12),
    topTechs: [...techs.values()].sort((a, b) => b.revenue - a.revenue).slice(0, 8),
  };
}

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });

export default function Resumen({ orders }: { orders: ServiceOrder[] }) {
  const k = computeResumen(orders);
  const c = t.service.cockpit;
  return (
    <div className="space-y-4">
      <Card className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Kpi label={c.kpi.aro} value={fmtMxn.format(k.aro)} />
        <Kpi label={c.kpi.laborRate} value={fmtMxn.format(k.laborRate)} />
        <Kpi label={c.kpi.open} value={String(k.woCount - k.status.C)} />
        <Kpi label={c.kpi.revenueMtd} value={fmtMxn.format(k.totalMxn)} />
        <Kpi label={t.service.kpi.jobs} value={String(k.jobs)} />
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <Title className="text-base">{c.kpi.mix}</Title>
          <BarList className="mt-3" valueFormatter={(n: number) => fmtMxn.format(n)} data={[
            { name: c.laborLines, value: k.laborMxn }, { name: c.partsLines, value: k.partsMxn },
          ]} />
        </Card>
        <Card>
          <Title className="text-base">{t.service.categoryTitle}</Title>
          <BarList className="mt-3" valueFormatter={(n: number) => fmtMxn.format(n)} data={k.category} />
        </Card>
      </div>
      <Card>
        <Title className="text-base">{t.service.techTitle}</Title>
        <Table className="mt-2">
          <TableHead><TableRow>
            <TableHeaderCell>{t.service.techCols.tech}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.service.techCols.eff}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.service.techCols.revenue}</TableHeaderCell>
          </TableRow></TableHead>
          <TableBody>
            {k.topTechs.map((r) => (
              <TableRow key={r.tech}>
                <TableCell>{r.name}</TableCell>
                <TableCell className="text-right tabular-nums">{r.act > 0 && r.est > 0 ? `${Math.round(r.est / r.act * 100)}%` : '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtMxn.format(r.revenue)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {orders.length === 0 && <Text>{t.service.empty}</Text>}
    </div>
  );
}
function Kpi({ label, value }: { label: string; value: string }) {
  return (<div><div className="text-tremor-content text-xs">{label}</div>
    <div className="text-tremor-content-strong text-lg font-semibold tabular-nums">{value}</div></div>);
}
